import NavigationBar from '../components/NavigationBar';
import Footer from '../components/Footer';
import { Check, Users, Calendar, Car, Crown } from 'lucide-react';

const WhoIsItForPage = ({ user }) => {
  const models = [
    {
      title: 'Fractional Ownership',
      subtitle: 'Individual Car',
      icon: <Users className="w-12 h-12 text-gold" />,
      description: 'Share ownership of a single luxury or performance car with other members.',
      ideal: [
        'Your usage is occasional—weekends, special occasions, or business meetings',
        'You desire a luxury car without full capital commitment',
        'You travel frequently to different cities and want to fractionally own & use a luxury car there',
        'You value convenience—insurance, maintenance, and documentation handled for you',
        'You want to diversify your portfolio of cars into multiple categories'
      ],
      perfectFor: [
        'Entrepreneurs',
        'Young achievers',
        'Senior executives',
        'Frequent travellers',
        'Celebrities',
        'Automotive Enthusiasts',
        'NRIs visiting India seasonally',
        'Luxury-lifestyle seekers'
      ],
      color: 'border-gold'
    },
    {
      title: '360-Days Curation',
      subtitle: 'Complete Annual Package',
      icon: <Calendar className="w-12 h-12 text-gold" />,
      description: 'Access to 3 premium cars throughout the year in Commuter, Outstation, and Weekender segments',
      ideal: [
        'You need a luxury car every day of the year',
        'You prefer variety, rotating between multiple cars for different use cases',
        'You value absolute flexibility based on mood, purpose, and season',
        'You want the privileges of owning multiple cars without liabilities',
        'You seek a hassle-lifestyle with flexibility'
      ],
      perfectFor: [
        'Business owners',
        'Senior Leaders, CEOs, MDs',
        'HNIs (High Net-worth Individuals)',
        'Celebrities',
        'Automotive Enthusiasts',
        'Luxury-lifestyle seekers'
      ],
      color: 'border-gold'
    },
    {
      title: 'The Haute Club (Coming Soon)',
      subtitle: 'An ultra exclusive members only club',
      icon: <Crown className="w-12 h-12 text-gold" />,
      description: 'One membership for multiple luxury assets across cities & borders',
      ideal: [
        'You want to use multiple assets, such as luxury cars, vacation homes, and yachts, without fully investing in each',
        'You frequently travel across the country and want the convenience of your luxury assets wherever you go',
        'You\'re looking at a return on your investment while using your assets',
        'You want a complete luxury lifestyle in one comprehensive package',
        'You want to be part of an ultra-exclusive, limited-members\' club/community'
      ],
      perfectFor: [
        'Ultra HNIs (High Net-worth Individuals)',
        'Leaders',
        'Kings & Queens'
      ],
      color: 'border-gold'
    },
    {
      title: 'Outright Purchase',
      subtitle: 'Full Ownership',
      icon: <Car className="w-12 h-12 text-gold" />,
      description: 'Traditional car ownership—buy, customise, and own your vehicle completely.',
      ideal: [
        'Your usage is daily and consistent, with high annual mileage',
        'You prefer full control—customisation, modifications, without shared access',
        'You need predictable availability for last-minute or high-frequency use',
        'Your car is an emotional extension of your personality or its a collectible',
        'You buy luxury cars in your company to claim depreciation & tax benefits'
      ],
      perfectFor: [
        'Families with daily commute needs',
        'Frequent long-distance travellers',
        'Professionals with fixed routines',
        'Individuals with dynamic schedule',
        'Car collectors'
      ],
      color: 'border-gold'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFBF5' }}>
      <NavigationBar user={user} />
      
      {/* Hero Section */}
      <section className="pt-12 sm:pt-16 md:pt-20 pb-4 sm:pb-6 md:pb-8 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
            Which Model is Right for You?
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            The Haute Circle offers four distinct pathways to luxury automotive access. 
            Choose the model that best fits your lifestyle, usage patterns, and preferences.
          </p>
        </div>
      </section>

      {/* Comparison Cards */}
      <section className="pt-2 sm:pt-3 md:pt-4 pb-6 sm:pb-8 md:pb-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto space-y-8 sm:space-y-12">
          {models.map((model, index) => (
            <div 
              key={index} 
              className={`bg-white rounded-lg shadow-xl overflow-hidden border-2 ${model.color} hover:shadow-2xl transition-shadow duration-300`}
            >
              <div className="p-4 sm:p-6 md:p-8 lg:p-12">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-6 sm:mb-8">
                  <div className="flex-shrink-0">
                    {model.icon}
                  </div>
                  <div>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">{model.title}</h2>
                    <p className="text-base sm:text-lg md:text-xl text-gold font-semibold">{model.subtitle}</p>
                    <p className="text-gray-600 mt-2 sm:mt-3 text-sm sm:text-base md:text-lg">{model.description}</p>
                  </div>
                </div>

                {/* Content Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                  {/* Left Column - Ideal When */}
                  <div>
                    <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                      <span className="text-gold">✓</span> Ideal When:
                    </h3>
                    <ul className="space-y-3">
                      {model.ideal.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                          <span className="text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Right Column - Perfect For */}
                  <div>
                    <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                      <span className="text-gold">★</span> Perfect For:
                    </h3>
                    <div className="bg-cream/50 p-6 rounded-lg">
                      <ul className="space-y-3">
                        {model.perfectFor.map((person, idx) => (
                          <li key={idx} className="flex items-center gap-3">
                            <span className="w-2 h-2 bg-gold rounded-full flex-shrink-0"></span>
                            <span className="text-gray-800 font-medium">{person}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Decision Helper */}
      <section className="py-20 px-6 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Still Unsure?</h2>
          <p className="text-xl mb-8 text-gray-300">
            Our concierge team is here to help you choose the perfect model for your lifestyle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/register-interest" 
              className="px-8 py-4 rounded-lg font-semibold text-lg transition-all inline-block text-black hover:opacity-90"
              style={{ backgroundColor: '#D4AF37' }}
            >
              Express Interest
            </a>
            <a 
              href="/support" 
              className="bg-white hover:bg-gray-100 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-all inline-block"
            >
              Contact Support
            </a>
          </div>
        </div>
      </section>

      {/* Quick Comparison Table */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12">Quick Comparison</h2>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full bg-white rounded-lg shadow-lg overflow-hidden min-w-[600px]">
              <thead className="bg-black text-white">
                <tr>
                  <th className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm md:text-base">Feature</th>
                  <th className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm md:text-base">Fractional Investment</th>
                  <th className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm md:text-base">360-Days Curation</th>
                  <th className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm md:text-base">Outright Purchase</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 font-semibold text-xs sm:text-sm md:text-base">Capital Investment</td>
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 text-center text-green-600 text-xs sm:text-sm md:text-base">Low</td>
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 text-center text-yellow-600 text-xs sm:text-sm md:text-base">Medium</td>
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 text-center text-red-600 text-xs sm:text-sm md:text-base">High</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold">Usage Days/Year</td>
                  <td className="px-6 py-4 text-center">30-90 days</td>
                  <td className="px-6 py-4 text-center">360 days</td>
                  <td className="px-6 py-4 text-center">365 days</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold">Vehicle Variety</td>
                  <td className="px-6 py-4 text-center">1 car category</td>
                  <td className="px-6 py-4 text-center">3 different cars</td>
                  <td className="px-6 py-4 text-center">1 car (your choice)</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold">Maintenance</td>
                  <td className="px-6 py-4 text-center text-green-600">✓ Included</td>
                  <td className="px-6 py-4 text-center text-green-600">✓ Included</td>
                  <td className="px-6 py-4 text-center text-red-600">Your responsibility</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold">Insurance</td>
                  <td className="px-6 py-4 text-center text-green-600">✓ Included</td>
                  <td className="px-6 py-4 text-center text-green-600">✓ Included</td>
                  <td className="px-6 py-4 text-center text-red-600">Your responsibility</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold">Flexibility</td>
                  <td className="px-6 py-4 text-center">Scheduled</td>
                  <td className="px-6 py-4 text-center text-green-600">High</td>
                  <td className="px-6 py-4 text-center text-green-600">Complete</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold">Best For</td>
                  <td className="px-6 py-4 text-center">Occasional users</td>
                  <td className="px-6 py-4 text-center">Daily variety seekers</td>
                  <td className="px-6 py-4 text-center">Daily fixed users</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default WhoIsItForPage;
